/**
 * System Control Notification Event Handler Service
 *
 * Handles incoming system notification events and coordinates operations.
 * This service listens to the SystemControlNotificationService event stream and
 * triggers appropriate refresh actions.
 */

import { Injectable, inject, DestroyRef, signal } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, EMPTY } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';

import { SystemControlNotificationService } from './system-control.service';
import { SettingsCommunicationEvent } from './notification.types';
import { MaintenanceService } from '@data-access/system-controls/maintenance';
import { AuthService } from '../../../auth/auth.service';
import { FEATURE_FLAGS_SIGNAL_STORE } from '@data-access/system-controls/feature-flags';

@Injectable({ providedIn: 'root' })
export class SystemControlNotificationEventHandler {
  private readonly __notificationService = inject(
    SystemControlNotificationService,
  );
  private readonly __router: Router = inject(Router);
  private readonly __destroyRef = inject(DestroyRef);
  private readonly __snackBar = inject(MatSnackBar);
  private readonly __maintenanceService = inject(MaintenanceService);
  private readonly __authService = inject(AuthService);
  private readonly __store = inject(Store);
  private readonly __featureFlagsStore = inject(FEATURE_FLAGS_SIGNAL_STORE);

  private readonly $isHandling = signal<boolean>(false);
  private readonly $currentUserId = signal<string | null>(null);

  public readonly isHandling = this.$isHandling.asReadonly();
  public readonly currentUserId = this.$currentUserId.asReadonly();

  /**
   * Start handling events for a workspace
   *
   * This connects to the workspace WebSocket and starts listening for events.
   * Events are filtered based on current route and trigger appropriate refresh
   * actions.
   * @returns Observable that completes when handling stops
   */
  startHandling(userId: string): Observable<void> {
    if (this.$isHandling() && this.$currentUserId() === userId) {
      console.debug(
        `[SettingsCommunication] Already handling events :: ${userId}`,
      );
      return EMPTY;
    }

    this.$isHandling.set(true);
    this.$currentUserId.set(userId);

    // Connect to workspace WebSocket
    return this.__notificationService.connect().pipe(
      switchMap(() => {
        // Listen to events and handle them
        this.__notificationService.events$
          .pipe(takeUntilDestroyed(this.__destroyRef))
          .subscribe((event: SettingsCommunicationEvent) =>
            this.__handleEvent(event),
          );

        return EMPTY;
      }),
      takeUntilDestroyed(this.__destroyRef),
    );
  }

  /**
   * Stop handling events
   */
  stopHandling(): void {
    if (!this.$isHandling()) {
      return;
    }

    console.log('[SettingsCommunication] Stopping event handling');

    this.__notificationService.disconnect();
    this.$isHandling.set(false);
    this.$currentUserId.set(null);
  }

  /**
   * Handle a notification event
   *
   * This is the main event handler that routes events to appropriate refresh logic.
   */
  private __handleEvent(event: SettingsCommunicationEvent): void {
    console.log('[SettingsCommunication] Handling notification event:', event);

    // Filter out events initiated by current user
    if (event.initiating_user_id === this.$currentUserId()) {
      console.log('[SettingsCommunication] Ignoring self-initiated event');
      return;
    }

    switch (event.event) {
      case 'maintenance_mode_enabled':
        this.__handleMaintenanceEnabled(event);
        break;
      case 'maintenance_mode_disabled':
        this.__handleMaintenanceDisabled(event);
        break;
      case 'maintenance_mode_cancelled':
        this.__handleMaintenanceCancelled(event);
        break;
      case 'maintenance_mode_scheduled':
        this.__handleMaintenanceScheduled(event);
        break;
      case 'feature_flag_updated':
        this.__handleFeatureFlagUpdated(event);
        break;
      case 'system_operation_completed':
        this.__handleSystemOperationCompleted(event);
        break;
      case 'logout_user':
        this._handleLogoutUser(event);
        break;
      default:
        console.warn(
          '[SettingsCommunication] Unknown event type:',
          event.event,
        );
    }
  }

  private __handleMaintenanceEnabled(event: SettingsCommunicationEvent): void {
    console.log('[SettingsCommunication] Handled maintenance enabled');
    console.log('Event', event);

    // Get current user to check if they can bypass maintenance
    this.__store
      .select((state: any) => state.user?.currentUser)
      .pipe(
        filter((user) => !!user),
        take(1),
      )
      .subscribe(async (user) => {
        const canBypass = this.__maintenanceService.canBypassMaintenance(user);

        if (!canBypass) {
          // Non-admin user - show warning and redirect to maintenance page
          this.__snackBar.open(
            'Maintenance Mode has been enabled. Redirecting to maintenance page...',
            'Dismiss',
            {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['maintenance-warning-snackbar'],
            },
          );

          // Wait 3 seconds to let user see the message, then redirect to maintenance page
          setTimeout(() => {
            console.log(
              '[SettingsCommunication] Redirecting non-admin user to maintenance page',
            );

            this.__authService.addLogoutInsights(
              'MaintenanceModeEnabledWebSocket',
              {
                event_type: event.event,
                timestamp: event.timestamp,
                action: 'redirected_to_maintenance',
              },
            );

            // Navigate to maintenance page
            this.__router.navigate(['/maintenance'], { replaceUrl: true });
          }, 3000);
        } else {
          // Admin user - just show notification
          this.__snackBar.open(
            'Maintenance Mode has been enabled. You can still access the system as an admin.',
            'Dismiss',
            {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            },
          );

          // If on system controls page, refresh to show updated toggle
          if (
            this.__router.url.includes('/admin/configurations/system-controls')
          ) {
            window.location.reload();
          }
        }
      });
  }

  private __handleMaintenanceDisabled(
    _event: SettingsCommunicationEvent,
  ): void {
    console.log('[SettingsCommunication] Handled maintenance disabled');

    // Show notification to user
    this.__snackBar.open(
      'Maintenance Mode has been disabled. The system is now available.',
      'Dismiss',
      {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
      },
    );

    // Refresh current page to ensure fresh state
    if (this.__router.url.includes('/admin/configurations/system-controls')) {
      window.location.reload();
    }
  }

  private __handleMaintenanceCancelled(
    _event: SettingsCommunicationEvent,
  ): void {
    console.log('[SettingsCommunication] Handled maintenance cancelled');

    this.__snackBar.open(
      'Scheduled maintenance has been cancelled.',
      'Dismiss',
      {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
      },
    );
  }

  private __handleMaintenanceScheduled(
    _event: SettingsCommunicationEvent,
  ): void {
    console.log('[SettingsCommunication] Handled maintenance scheduled');

    this.__snackBar.open(
      'Maintenance has been scheduled. You will be notified before it begins.',
      'Dismiss',
      {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
      },
    );
  }

  private __handleFeatureFlagUpdated(_event: SettingsCommunicationEvent): void {
    console.log(
      '[SettingsCommunication] Feature flag updated, reloading flags',
    );

    // Reload all feature flags from backend
    this.__featureFlagsStore.loadFeatureFlags();
  }

  private __handleSystemOperationCompleted(
    _event: SettingsCommunicationEvent,
  ): void {
    console.log('[SettingsCommunication] System operation completed');

    // Show notification to admins
    this.__snackBar.open(
      'A system operation has completed. Check the System Settings panel for details.',
      'Dismiss',
      {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
      },
    );

    // If on system controls page, you might want to refresh the operation history
    // This can be implemented later if needed
  }

  private _handleLogoutUser(_event: SettingsCommunicationEvent): void {
    console.log(
      '[SettingsCommunication] Received logout_user event, logging out targetted user',
    );

    const targetUserId = _event.target_user_id ?? null;

    // Only log out if the event is targeting the current user
    if (targetUserId === this.$currentUserId()) {
      this.__router.navigate(['/auth/logout'], { replaceUrl: true });
    }
  }
}
